<script setup lang="ts">
// -------------------------------
// 🔹 BaseProduct
// -------------------------------
/**
 * Composant représentant un produit.
 *
 * Props :
 * - IBaseProductProps : informations du produit (nom, prix, image, etc.)
 *
 * Événements :
 * - "HandleClick" : émis lorsque le produit est cliqué
 *
 * Exemple d'utilisation :
 * ```vue
 * <BaseProduct :product="product" @HandleClick="onProductClick" />
 * ```
 */
import type { IBaseProductProps } from '@/types/base_product.types';
import { formatPrice } from '@/utils/formatPrice';

defineProps<IBaseProductProps>();
const emit = defineEmits(['HandleClick']);

const handleClick = (): void => {
  emit('HandleClick');
};
</script>

<template>
  <section class="cursor-pointer space-y-1" @click="handleClick">
    <div class="bg-fond-beige flex flex-col rounded-2xl p-4">
      <div class="h-[102px] w-[210px]">
        <img
          v-show="typeof img !== 'undefined' && img"
          :src="img"
          :alt="alt"
          class="aspect-square h-full w-full object-contain"
        />
      </div>
      <div
        v-show="promotionTitle"
        class="text-12px font-tt-normal-Normal text-selection flex flex-row items-center gap-2"
      >
        <p class="bg-white p-2 opacity-[0.8] backdrop-blur-sm">
          {{ promotionTitle }}
        </p>

        <div
          class="flex flex-row items-center gap-1 bg-white p-2 opacity-[0.8] backdrop-blur-sm"
        >
          <p>{{ promotionValue }}</p>
          <p>{{ promotionDesc }}</p>
        </div>
      </div>
    </div>

    <div class="w-48 space-y-2 py-2">
      <p
        class="text-14px text-grand-text font-tt-medium-Normal line-clamp-1 leading-normal text-ellipsis"
      >
        {{ designation }}
      </p>
      <p
        class="font-tt-normal-Normal text-secondary-text text-14px line-clamp-2 max-w-full overflow-hidden leading-[19px] text-ellipsis"
      >
        {{ descritpion }}
      </p>

      <div class="flex flex-row items-center justify-between">
        <p
          class="text-selection text-12px line-clamp-1 h-[21px] flex-1 overflow-hidden bg-white leading-normal text-ellipsis opacity-[0.8] backdrop-blur-sm"
        >
          {{ categorie }}
        </p>
        <p class="text-14px font-tt-medium-Normal leading-normal">
          {{ formatPrice(Number(price)) }}
        </p>
      </div>
    </div>
  </section>
</template>
